import { WeatherLayerInfo, MgLayersBase, ChartDepths, LayerGroup, ChartLayersBase } from './mg-layers';
import { LayersFactory } from '@maersk-global/map-layers';
import { default as PlayerCore } from '@maersk-global/player-core';
import { AuthService } from '../services/auth/auth-service';
import { WeatherModel, ForecastInfo } from '../models/weather-model';
import ImageWMSSource from 'ol/source/ImageWMS';
import Layer from 'ol/layer/Layer';
import { VoyageStyle } from '../../local_modules/voyage-style/src/voyage-style';
import { MgChartLayersImpl } from './mg-chart-layers';
import { WBChartLayersImpl } from './wb-chart-layers';
import { OSM, Source } from 'ol/source';
import { Tile } from 'ol/layer';
import { VoyageImoModel } from '../models/voyage-imo-model';
import { apiCallTracker } from '../utils/api-call-tracker';

const { 
    vesselOnCloud: isVesselOnCloud = false, 
    projection: _PROJECTION = '',
    services: { weather = '', weatherwms: weatherwmsConfig = '', maerskchartsrv = '' }, 
    apiBasePath: basePath = '',
    scope = '',
    consumerKey = '',
    xRequestor = '',
    showMapViewOptions = false,
} = (window as any).appenv;

const weatherApiConfig = isVesselOnCloud ? { ...weather, scope } : weather;
const weatherwms = isVesselOnCloud ? basePath + weatherApiConfig.baseurl : weatherwmsConfig.baseurl;

const _WeatherElements: [string,string,LayerGroup][] = [
    ['Wind50M','Wind 50 meters',LayerGroup.Wind],
    ['Wind10M','Wind 10 meters',LayerGroup.Wind],
    ['WindGusts50M','Wind gusts 50 meters',LayerGroup.Wind],
    ['WindGusts10M','Wind gusts 10 meters',LayerGroup.Wind],
    ['RiskWindSpeed','Risk wind speed',LayerGroup.Wind],

    ['WavePeriod','Wave period',LayerGroup.SeaAndSwell],
    ['SwellPeriod','Swell period',LayerGroup.SeaAndSwell],
    ['Wave','Wave',LayerGroup.SeaAndSwell],
    ['Swell','Swell',LayerGroup.SeaAndSwell],
    ['RiskWaveHeight','Risk wave height',LayerGroup.SeaAndSwell],

    ['Pressure','Pressure',LayerGroup.PressureAndTemp],
    ['AirTemperature','Air temperature',LayerGroup.PressureAndTemp],
    ['SeaWaterTemperature','Sea water temperature',LayerGroup.PressureAndTemp],

    ['DewPoint','Dew point',LayerGroup.Others],
    ['FreezingLevel','Freezing level',LayerGroup.Others],
    ['HPa500','HPa 500',LayerGroup.Others],
    ['Icing','Icing',LayerGroup.Others],
    ['Precipitation','Precipitation',LayerGroup.Others],
    ['RelativeHumidity','Relative humidity',LayerGroup.Others],
    ['IceConcentration','Ice concentration',LayerGroup.Others],
    ['SeaWaterSalinity','Sea water salinity',LayerGroup.Others],
    ['Visibility','Visibility',LayerGroup.Others],
    ['Currents','Currents',LayerGroup.Others],
];

const _WeatherView = [
    'area',
    'line',
    'grid'
];

const _NonGridWeatherElements: [string,string,LayerGroup][] = [
    ['IceBergPosition','Icebergs',LayerGroup.Others],
    ['Hurricanes','Hurricanes',LayerGroup.Others]
    // Disabled as these only work for two hours a day, see https://jiraeu.epam.com/browse/MPM-1655
    //['WeatherFrontData','Weather fronts',LayerGroup.Others],
    //['HighLowPressureSymbols','Pressure symbols',LayerGroup.Others]
];


// EPSG:4326 - Unprojected
// EPSG:3395 - World Mercator
// EPSG:3857 - Pseudo/Web Mercator

interface StoreObject
{
    depths: ChartDepths;
    borderLayersEnabled: boolean;
    list: WeatherLayerInfo[];
}

interface LayerVisibility {
    element: string,
    area: boolean,
    line: boolean,
    grid: boolean
}


export class MgLayersImpl extends MgLayersBase
{
    constructor(playerCore: PlayerCore)
    {
        super();

        this._playerCore = playerCore;

        this._onWeatherUpdated = this._onWeatherUpdated.bind(this);
        this._onTimeUpdate = this._onTimeUpdate.bind(this);
        this._onStylesChanged = this._onStylesChanged.bind(this);
    }

    get layers() {
        return this._layers;
    }

    get list() {
        if (!this._initialized) return [];

        const res: WeatherLayerInfo[] = [];

        _WeatherElements.forEach(element => {
            const el: WeatherLayerInfo = {element: element[0], name: element[1], group: element[2]};
            _WeatherView.forEach(viewType => {
                const layer = this._weatherLayersMap.get(`${element[0]}_${viewType}`);
                if (layer) {
                    el[(<"area"|"line"|"grid">viewType)] = {
                        visible: layer.getVisible(),
                        opacity: layer.getOpacity()
                    };
                }
            });
            res.push(el);
        });

        _NonGridWeatherElements.forEach(element => {
            const el: WeatherLayerInfo = {element: element[0], name: element[1], group: element[2]};
            const layer = this._weatherLayersMap.get(`${element[0]}_nongrid`);
            el.nongrid = {
                visible: layer && layer.getVisible() ? true : false
            };
            res.push(el);
        });

        return res;
    }

    get chartDepths() {
        const mapLayerModelData = localStorage.getItem('map-layer-model');
        const depthsFromLocalStorage = mapLayerModelData ? JSON.parse(mapLayerModelData).depths : this._depths;
        return depthsFromLocalStorage;
    }

    get borderLayersEnabled() {
        return this._borderLayersEnabled;
    }

    setVisible(element: string, type: string, visible: boolean) {
        const weatherLayer = this._weatherLayersMap.get(`${element}_${type}`);
        
        if (weatherLayer) {
            weatherLayer.setVisible(visible);
            this._setBorderLayer(element);
            this._store();
        }
    }

    setOpacity(element: string, type: string, opacity: number) {
        const layer = this._weatherLayersMap.get(`${element}_${type}`);
        if (layer) {
            layer.setOpacity(opacity);
            this._store();
        }
    }

    toggleBorderLayers(visible: boolean) {
        this._borderLayersEnabled = visible;
        this.list.forEach((el)=> {
            this._setBorderLayer(el.element)
        })
        this._store();
    }

    setChartDepths(depths: ChartDepths) {
        this._depths = depths;

        this._chartLayers?.setChartDepths(depths);

        this._store();
    }

    async initialize(mapConfig: any) {
        WeatherModel!.addEventListener("weather-updated", this._onWeatherUpdated);
        this._playerCore.addEventListener("timeupdate", this._onTimeUpdate);
        VoyageStyle.addEventListener("styles-changed", this._onStylesChanged)
        this._setForecastInfo(WeatherModel!.latestForecastInfo);
        const forecastVersion = this._forecastInfo ? this._forecastInfo.name : WeatherModel!.climaticForecast;
        this._usingClimaticForecast = forecastVersion === WeatherModel!.climaticForecast;
        this._onStylesChanged();
        const authLoader = async (url: string, signal: AbortSignal) => {

            let accessToken;

            if ((<any>window)["appenv"].authRequired || isVesselOnCloud) accessToken = await AuthService.getAccessToken(weatherApiConfig.scope);

            const response = await fetch(url, {
                mode: 'cors',
                cache: 'no-cache',
                method: 'GET',
                signal: signal,
                headers: accessToken ? {
                    'Authorization': `Bearer ${accessToken}`,
                    'consumer-key': consumerKey, 
                    'X-Requestor': xRequestor,
                } : {}
            });

            // Track weather layer API calls
            if (isVesselOnCloud) {
                try {
                    const requestBytes = 0; // GET request with no body
                    const responseBytes = response.headers.get('content-length') 
                        ? Number(response.headers.get('content-length')) 
                        : 0; // For streaming responses, we can't easily get the size
                    
                    apiCallTracker.incrementCallCount(requestBytes, responseBytes, url);
                } catch (error) {
                    console.warn('Failed to track weather layer API call:', error);
                }
            }

            return response;
        }

        this._layersFactory = new LayersFactory({
            chartWMSUrl: maerskchartsrv,
            weatherWMSUrl: weatherwms + '/api/{version}/{format}/{forecast}/{element}?imo={imo}&&view={view}&time={time}&z={z}&x={x}&y={y}',
            weatherNonGridUrl: weatherwms + '/api/{version}/{format}/nongrid/{forecast}/{element}?imo={imo}',
            weatherBorderWMSUrl: weatherwms + '/api/{version}/{format}/{forecast}/{element}/areas?imo={imo}',
            featureLoader: authLoader,
            player: this._playerCore,
        });

        const { encTileServer = false, worldMap = true  } = (<any>window)["appenv"].featureToggles; 
        
        if (encTileServer || worldMap) {
            this._chartLayers = new WBChartLayersImpl();
        } else {
            this._chartLayers = new MgChartLayersImpl(this._layersFactory);
        }
        if(isVesselOnCloud && showMapViewOptions) {
            if(mapConfig.simplified == "true"){
             this._layers.push(new Tile({source: new OSM({
                url: "https://tile.openstreetmap.de/{z}/{x}/{y}.png",
                crossOrigin: 'anonymous'
             })}));
            } else {
                await this._chartLayers.initialize(mapConfig);
                this._layers.push(...this._chartLayers.layers);
            }
        } else {
            await this._chartLayers.initialize(mapConfig);
            this._layers.push(...this._chartLayers.layers);
        }
            

        // grid, isoline and area layers
        _WeatherElements.forEach(element => {

            const border = this._createBorderLayer(element, forecastVersion);
            const area = this._createWeatherWMSLayer(element, 'area', forecastVersion)
            const line = this._createWeatherWMSLayer(element, 'line', forecastVersion)
            const grid = this._createWeatherWMSLayer(element, 'grid', forecastVersion)
            
            this._layers.push(border);
            this._layers.push(area);
            this._layers.push(line);
            this._layers.push(grid);
            
            this._borderLayersMap.set(element[0], border)
            this._weatherLayersMap.set(`${element[0]}_area`, area);
            this._weatherLayersMap.set(`${element[0]}_line`, line);
            this._weatherLayersMap.set(`${element[0]}_grid`, grid);

            this._layersVisibility.push({
                element: element[0],
                area: false,
                line: false,
                grid: false
            })

        });

        _NonGridWeatherElements.forEach(element => {   
            const defaultParams = {
                element: element[0],
                forecast: forecastVersion,
                imo: VoyageImoModel?.imo,
            };

            const params = isVesselOnCloud ? {
                ...defaultParams,
                imo: VoyageImoModel?.imo,
            } : defaultParams;
            
            const layer = this._layersFactory!.createWeatherVectorLayer({
                title: `${element[1]}`,
                timeBucketMs: 'hourly',
                projection: _PROJECTION,
                maxZoom: 10,
                params,
            });

            layer.setVisible(false);
            this._layers.push(layer);
            this._weatherLayersMap.set(`${element[0]}_nongrid`, layer);
        });

        this._initialized = true;
        this._load();
    }

    zoomLevelChanged(zoom: number) {
        this._chartLayers?.zoomLevelChanged(zoom);
    }


    private _playerCore: PlayerCore;
    private _initialized: boolean = false;
    private _layers: Layer<Source>[] = [];
    private _forecastInfo: ForecastInfo|null = null;
    private _forecastTimeframe: [number, number] = [ -1, -1 ];
    private _usingClimaticForecast: Boolean = false;
    private _depths: ChartDepths = {
        safetyContour: 20,
        shallowContour: 15,
        deepContour: 30,
        safetyDepth: 20
    };
    private _weatherLayersMap: Map<string, Layer<Source>> = new Map();
    private _layersFactory: LayersFactory|null = null;
    private _chartLayers: ChartLayersBase|null = null;
    private _layersVisibility: LayerVisibility[] = [];
    private _borderLayersMap: Map<string, Layer<Source>> = new Map();
    private _borderLayersEnabled: boolean = false;

    private _store() {
        const res: StoreObject = {
            depths: this._depths,
            borderLayersEnabled: this._borderLayersEnabled,
            list: this.list
        };
        localStorage.setItem('map-layer-model', JSON.stringify(res));
        sessionStorage.setItem('map-layer-model', JSON.stringify(res));
    }

    private _load() {
        try {
            const res: StoreObject = JSON.parse(sessionStorage.getItem('map-layer-model') || localStorage.getItem('map-layer-model') || "null");
            if (!res || !res.list || !res.depths) {
                this._store();
                throw new Error("failed to load map layers from cache")
            }
            this._borderLayersEnabled = res.borderLayersEnabled;
            this._depths=res.depths;

            // set layers visibility
            for (let i = 0; i < res.list.length; i++) {
                const el = res.list[i];
                if (el.grid && el.grid!.visible) {
                    this.setVisible(el.element, 'grid', true)
                }
                if (el.line && el.line!.visible) {
                    this.setVisible(el.element, 'line', true)
                }
                if (el.area && el.area!.visible) {
                    this.setVisible(el.element, 'area', true)
                }
                if (el.nongrid) {
                    if (el.nongrid!.visible) {
                        this.setVisible(el.element, 'nongrid', true)
                    }
                    if (el.nongrid.opacity) {
                        this.setOpacity(el.element, 'nongrid', el.nongrid.opacity);
                    }
                }
            }
            this.setChartDepths(res.depths);
        }
        catch (error) {
            this._store();
        }

        this.dispatchEvent(new Event('change'));
    }


    private _setForecastInfo(info: ForecastInfo|null) {
        this._forecastInfo = info;
        if (info) {
            this._forecastTimeframe = [ new Date(info.forecast_from).getTime(), new Date(info.forecast_to).getTime() ];
        }
    }

    private _onWeatherUpdated() {
        const forecastInfo = WeatherModel!.latestForecastInfo;

        if (forecastInfo && (!this._forecastInfo || this._forecastInfo.name !== forecastInfo.name)) {
            this._setForecastInfo(forecastInfo);

            // Check if new forecast makes us go from climatic to real forecast
            if (this._usingClimaticForecast &&
                this._playerCore.currentTime >= this._forecastTimeframe[0] && this._playerCore.currentTime <= this._forecastTimeframe[1])
            {
                this._usingClimaticForecast = false;
            }

            for(let [key, layer] of this._weatherLayersMap) {
                if (key.endsWith("nongrid")) {
                    (<ImageWMSSource>layer.getSource()).updateParams({ forecast: this._forecastInfo!.name });
                    // These sources don't automatically refresh when setting a new forecast - explicitly refresh here
                    (<ImageWMSSource>layer.getSource()).refresh();
                } else if (!this._usingClimaticForecast) {
                    (<any>layer.getSource()).timeBucketType = 'hourly';
                    (<ImageWMSSource>layer.getSource()).updateParams({ forecast: this._forecastInfo!.name });
                }
            }

            for (let [key, layer] of this._borderLayersMap) {
                (<ImageWMSSource>layer.getSource()).updateParams({ forecast: this._forecastInfo!.name });
                (<ImageWMSSource>layer.getSource()).refresh();
            }
        }
    }

    private _onTimeUpdate()
    {
        if (this._forecastInfo)
        {
                if (this._playerCore.currentTime >= this._forecastTimeframe[0] && this._playerCore.currentTime <= this._forecastTimeframe[1])
                {
                    if (this._usingClimaticForecast)
                    {
                        // Switch from climatic forecast to real forecast
                        for(const [key, layer] of this._weatherLayersMap)
                        {
                            // non-grid elements never use climatic weather, so skip those
                            if (!key.endsWith("nongrid"))
                            {
                                (<any>layer.getSource()).timeBucketType = 'hourly';
                                // Let the layers apply the time change before changing the forecast parameter
                                setTimeout( () => {
                                    (<ImageWMSSource>layer.getSource()).updateParams({ forecast: this._forecastInfo!.name });
                                });
                            }
                        }
                        this._usingClimaticForecast = false;
                        
                        // refresh borders visibility (should be visible when using real forecast)
                        this.list.forEach((el)=> {
                            this._setBorderLayer(el.element)
                        })
                    }
                }
                else if (!this._usingClimaticForecast)
                {
                    // Switch from real forecast to climatic
                    for(const [key, layer] of this._weatherLayersMap)
                    {
                        // non-grid elements can't use climatic weather, so skip those
                        if (!key.endsWith("nongrid"))
                        {
                            (<any>layer.getSource()).timeBucketType = 'monthly';
                            // Let the layers apply the time change before changing the forecast parameter
                            setTimeout( () => {
                                (<ImageWMSSource>layer.getSource()).updateParams({ forecast: WeatherModel!.climaticForecast });
                            });
                        }
                    }
                    this._usingClimaticForecast = true;

                    // refresh borders visibility (should be invisible when using climatic forecast)
                    this.list.forEach((el)=> {
                        this._setBorderLayer(el.element)
                    })
                }
        }
    }

    private _onStylesChanged()
    {
        if (this._chartLayers) {
            this._chartLayers.setColorScheme(VoyageStyle.currentStyle);
        }
    }

    private _createWeatherWMSLayer 
    (
        element: [string, string, LayerGroup],
        viewType: string,
        forecastVersion: string,
    )
    {
         const defaultParams = {
              view: viewType,
              element: element[0],
              forecast: forecastVersion,
              additionalOptions: {
                  plot_extrema: true,
                  unit: 'kts'
              },
            unit: 'kts',
        };

        const params = isVesselOnCloud ? {
            ...defaultParams,
            imo: VoyageImoModel?.imo,
        } : defaultParams;

        const layer = this._layersFactory!.createWeatherWMSLayer
        ({
            title: `${element[1]} (${viewType})`,
            opacity: viewType === 'area' ? 0.5 : 1.0,
            timeBucketMs: 'hourly',
            preloadTimeMs: 2 * 60 * 60 * 1000,
            projection: _PROJECTION,
            maxZoom: 9,
            params,
        });

        layer.setVisible(false);
        return layer;
    }

    private _createBorderLayer
    (
        element: [string, string, LayerGroup],
        forecastVersion: string,
        visible = false
    ) 
    {
        // don't set title (ex. title: `${element.name} (${viewMode})`, )to hide layer on switcher control
        const layer = this._layersFactory!.createWeatherBorderWMSLayer({
            visible,
            projection: _PROJECTION,
            displayInLayerSwitcher: false,
            zIndex: 10,
            params: {
                view: 'border',
                element: element[0],
                forecast: forecastVersion,
            },
        });
        return layer;
    }

    private _setBorderLayer (element: string) {
        const borderLayer = this._borderLayersMap.get(element);

        if (borderLayer) {
            if(!this._borderLayersEnabled) {
                borderLayer.setVisible(false)
                
            } else {
                let viewTypesVisibilityInfo: WeatherLayerInfo;
                const layerList = this.list;

                // get visibility info of current weather element across grid, line and area view types
                for (let i = 0; i < layerList.length; i++) {
                    if (layerList[i].element === element) {
                        viewTypesVisibilityInfo = layerList[i];
                    }
                }

                if (!this._usingClimaticForecast && (viewTypesVisibilityInfo!.grid!.visible || viewTypesVisibilityInfo!.line!.visible  || viewTypesVisibilityInfo!.area!.visible) ) {
                    borderLayer.setVisible(true)
                }  else {
                    borderLayer.setVisible(false)
                }
            }
        }
    }
}
